import React, { useState } from "react";
import {
    CardNumberElement,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import PropTypes from "prop-types";

// components:
import CheckoutForm from "core/CheckoutForm";
import CheckoutHeader from "subscriptions/components/CheckoutHeader";
import SubscriptionLicenseBtns from "subscriptions/components/SubscriptionLicenseBtns";
import DisclaimerBodyText from "subscriptions/components/DisclaimerBodyText";

// reusable data / helpers:
import { useHistory } from "react-router";
import { getFormattedData } from "./helpers";
import { PRO_YEARLY_COST, PRO_MONTHLY_COST } from "subscriptions/data";
import { getCsrfCookieName } from "helpers/gazetteGetCsrfCookieName";
import { getCookie } from "helpers/gazetteGetCookie";
import { useCurrentUserContext } from "contexts/CurrentUserContext";
import { ICON_DETAIL_PAGE } from "data/links";
import {
    PRO_ANNUAL_DOWNLOAD,
    PRO_SINGLE_DOWNLOAD,
    QUERY_PURCHASED
} from "icons/helpers";

const DefaultCheckout = ({
    title,
    thumbnail,
    slugWithId,
    iconId,
    setPageLoading,
    isSubscription = "" // basically default to false
}) => {
    /* dummy props until we get the page laid out properly: */
    const response = {};
    const history = useHistory();

    const formattedData = getFormattedData({
        slugWithId,
        response
    });
    const {
        initialForm,
        hasSavedCard,
        itemPrice,
        orderSummaryTextLine1,
        orderSummaryTextLine2,
        iconDetailLink,
        isSubmitDisabled
    } = formattedData;
    /* */

    // set up/assign const states:
    const [saveCard, setSaveCard] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState(null);
    const [licenseSelection, setLicenseSelection] = useState(isSubscription);
    const stripe = useStripe();
    const elements = useElements();

    const {
        currentUser: { data }
    } = useCurrentUserContext();
    const { currentUser } = data;

    const submitFormWithNewCardInfo = async e => {
        e.preventDefault();

        const csrfCookieName = getCsrfCookieName();
        const csrftoken = getCookie({ name: csrfCookieName });

        if (!form.name) {
            return setFormError("Please fill out all required fields");
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        const { token, error } = await stripe.createToken(
            cardNumberElement,
            form
        );

        if (error) return setFormError(error.message);
        setPageLoading(true);

        let body;
        let routeURL;
        let queryPurchase;
        // if license selection is true it means its either pro-yearly or pro-monthly
        // if false its an empty string and we will move forward with a basic icon purchase
        if (licenseSelection) {
            queryPurchase = licenseSelection;
            routeURL = `/${currentUser.username}/subscribe/pro/`;
            body = {
                plan: licenseSelection,
                stripe_token: token.id
            };
        } else {
            queryPurchase = PRO_SINGLE_DOWNLOAD;
            routeURL = `/icon/${iconId}/purchase/`;
            body = {
                save_card: true,
                stripe_token: token.id,
                single_icon_price_cents: window._nounConfig.singleIconPriceCents
            };
        }

        fetch(routeURL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrftoken
            },
            body: JSON.stringify(body)
        }).then(response => {
            if (response.ok) {
                const iconNavURL =
                    ICON_DETAIL_PAGE.replace(":slugWithId", slugWithId) +
                    `?${QUERY_PURCHASED}=${queryPurchase}`;
                setPageLoading(false);
                history.replace(iconNavURL);
            }
            // TODO: what happens on fail
        });
    };

    const submitFormWithSavedCardInfo = e => {
        e.preventDefault();
        // TODO: add functionality
    };

    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setFormError(null);
    };

    const saveCardCheckboxOnClick = () => {
        setSaveCard(!saveCard);
    };

    // if user came from checkoutType + payment then we take the item
    // price which i believe is always 2.99. Need to check on that
    // otherwise we show cost based on toggle of licenseSelection
    const finalPrice = !isSubscription
        ? itemPrice
        : licenseSelection === PRO_ANNUAL_DOWNLOAD
        ? PRO_YEARLY_COST
        : PRO_MONTHLY_COST;

    // this will need to be in a useState if we decide to add PromoCode
    // later on. I feel like we will but for now requirements say no
    const subscriptionPrices = {
        yearly: PRO_YEARLY_COST,
        monthly: PRO_MONTHLY_COST
    };

    return (
        <>
            {isSubscription && (
                <>
                    {/* Might want to put these two in core/components */}
                    <CheckoutHeader />

                    <SubscriptionLicenseBtns
                        subscriptionPrices={subscriptionPrices}
                        licenseSelection={licenseSelection}
                        setLicenseSelection={setLicenseSelection}
                    />
                </>
            )}

            <CheckoutForm
                hasSavedCard={hasSavedCard}
                saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                saveCardInitialValue={saveCard}
                response={response}
                form={form}
                handleFormChange={handleFormChange}
                thumbnail={thumbnail}
                itemTitle={title}
                itemPrice={finalPrice}
                itemPriceOrderSummaryItem={`$${itemPrice}`}
                orderSummaryTextLine1={orderSummaryTextLine1}
                orderSummaryTextLine2={orderSummaryTextLine2}
                generalFormError={formError}
                isSubmitDisabled={isSubmitDisabled}
                submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                submitFormWithSavedCardInfo={submitFormWithSavedCardInfo}
                cancelLink={iconDetailLink}
                isSubscription={isSubscription}
            />

            {isSubscription && (
                <DisclaimerBodyText licenseSelection={licenseSelection} />
            )}
        </>
    );
};

DefaultCheckout.propTypes = {
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    slugWithId: PropTypes.string.isRequired,
    iconId: PropTypes.string.isRequired,
    isSubscription: PropTypes.string.isRequired,
    setPageLoading: PropTypes.func.isRequired
};

export default DefaultCheckout;
